import { useState } from 'react';
import { File, FileArchive, FileAudio, FileText, Film, Folder, Image as ImageIcon } from 'lucide-react';
import { thumbUrl } from '../api/client';
import { thumbhashUrl } from '../lib/thumbhash';
import { formatDuration } from '../lib/format';
import { cx } from './ui';
import type { FileDto } from '../api/types';

function KindIcon({ mime, className }: { mime: string; className?: string }) {
  const props = { className: cx('size-6', className), 'aria-hidden': true } as const;
  if (mime.startsWith('image/')) return <ImageIcon {...props} />;
  if (mime.startsWith('video/')) return <Film {...props} />;
  if (mime.startsWith('audio/')) return <FileAudio {...props} />;
  if (mime.startsWith('text/') || mime === 'application/pdf') return <FileText {...props} />;
  if (mime.includes('zip') || mime.includes('tar') || mime.includes('compressed')) {
    return <FileArchive {...props} />;
  }
  return <File {...props} />;
}

export function FolderIcon({ className }: { className?: string }) {
  return <Folder className={cx('size-6 text-brand', className)} aria-hidden />;
}

/**
 * A thumbnail, or the best stand-in available.
 *
 * Order of preference: the real derivative from imgproxy, the ThumbHash blur
 * while it loads, then a type icon. The blur matters most on a phone, where a
 * grid of grey holes is what a slow connection otherwise looks like.
 */
export function Thumb({
  file,
  size,
  className,
  rounded = true,
}: {
  file: FileDto;
  /** Requested width in CSS pixels; the URL is what gets cached, so keep it to a few values. */
  size: number;
  className?: string;
  rounded?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const blur = thumbhashUrl(file.thumbhash);
  const duration = formatDuration(file.durationMs);

  const shell = cx(
    'relative flex items-center justify-center overflow-hidden bg-surface-2',
    rounded && 'rounded-lg',
    className,
  );

  if (!file.thumbnailable || failed) {
    return (
      <div className={shell}>
        <KindIcon mime={file.mimeType} className="text-ink-dim" />
      </div>
    );
  }

  return (
    <div className={shell}>
      {blur ? (
        // Sits behind the real image and is simply covered when it paints, so
        // there is no flash of empty box and no layout shift.
        <img src={blur} alt="" aria-hidden className="absolute inset-0 size-full object-cover" />
      ) : null}
      <img
        src={thumbUrl(file.id, size, 'fill')}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="relative size-full object-cover"
      />
      {duration ? (
        <span className="absolute right-1 bottom-1 rounded bg-black/70 px-1 py-px text-[10px] font-medium text-white tabular-nums">
          {duration}
        </span>
      ) : null}
    </div>
  );
}
